import {match} from 'ts-pattern';

import {head} from 'ramda';

import {SaveSaleVehicleFinancingBody} from '@dms/api/vehicle';

import {FinancingTemplateDraft} from '../../types/FinancingTemplateDraft';
import {ensureActiveFinancingCell} from '../financingCells/ensureActiveFinancingCell';

/**
 * Builds the template-backed body a template selection produces. Insurance values belong to the
 * vehicle rather than the template, so they carry over from the body being replaced.
 * An empty template remains template-backed with no active variant, matching the backend contract.
 */
export function applyFinancingTemplate(
  template: FinancingTemplateDraft,
  currentBody: SaveSaleVehicleFinancingBody | null
): SaveSaleVehicleFinancingBody {
  const defaultBalloonPayment = match(template.type)
    .with(
      'BALLOON',
      () =>
        template.defaultBalloonPayment ??
        template.rateMatrix.cells.find((cell) => cell.balloonPayment !== null)?.balloonPayment ??
        null
    )
    .with('STANDARD', () => null)
    .exhaustive();
  const activeCell =
    template.rateMatrix.cells.find(
      (cell) =>
        cell.term === template.defaultTerm &&
        cell.downPayment === template.defaultDownPayment &&
        cell.balloonPayment === defaultBalloonPayment
    ) ??
    template.rateMatrix.cells.find((cell) => cell.balloonPayment === defaultBalloonPayment) ??
    head(template.rateMatrix.cells);

  return ensureActiveFinancingCell({
    type: template.type,
    mode: 'TEMPLATE',
    templateId: template.id,
    financialInstitutionCodeId: null,
    validTo: null,
    // Copied, not shared: `template` is RTK Query cache data (or a draft derived from it), and the
    // matrix editor replaces this array wholesale on every cell edit — holding the cache's own array
    // reference would let that in-place-looking replacement reach the cache instead of the draft.
    rateMatrix: {
      availableTerms: [...template.rateMatrix.availableTerms],
      cells: [...template.rateMatrix.cells],
    },
    activeTerm: activeCell?.term ?? null,
    activeDownPayment: activeCell?.downPayment ?? null,
    activeBalloonPayment: activeCell?.balloonPayment ?? null,
    processingFeeOverride: null,
    interestRateTypeOverride: null,
    vehicleTransferIncludedOverride: null,
    vehicleTransferAmountOverride: null,
    vehicleRegistrationIncludedOverride: null,
    vehicleRegistrationAmountOverride: null,
    vehicleDeliveryIncludedOverride: null,
    vehicleDeliveryAmountOverride: null,
    tradeInPossibleOverride: null,
    creditProtectionIncluded: template.creditProtectionAutoEnable,
    insuranceIncludedInCalculation: currentBody?.insuranceIncludedInCalculation ?? false,
    liabilityInsuranceMonthly: currentBody?.liabilityInsuranceMonthly ?? null,
    liabilityInsuranceDeductible: currentBody?.liabilityInsuranceDeductible ?? null,
    comprehensiveInsuranceMonthly: currentBody?.comprehensiveInsuranceMonthly ?? null,
    comprehensiveInsuranceDeductible: currentBody?.comprehensiveInsuranceDeductible ?? null,
  });
}
